import React, { PropsWithChildren, useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { IonContent, IonHeader, IonToolbar, IonItem, IonCard, IonCardContent, IonIcon, useIonAlert, IonNote, IonButton, IonSpinner, IonButtons, IonText, IonList, IonAvatar, IonLabel, IonPage, IonModal, useIonViewWillLeave, useIonViewWillEnter, useIonModal, IonNavLink, IonCardHeader, IonCardSubtitle, IonGrid, IonRow, IonCol, IonTitle } from '@ionic/react';
import { alertCircleOutline, arrowBackOutline, chevronDownOutline } from "ionicons/icons";
import { Asset, DelegateSigningParams } from "../../types/types";
import NumberFormat from "react-number-format";
import './TradeConfirmation.css';
import * as stellarApi from '../../services/stellar/horizon/data/data';
import { createTrustAndTradeTx, createTradeTx } from '../../services/stellar/horizon/dex/dex';
import * as sep7 from '../../services/stellar/sep7';
import * as stellarSdk from '@stellar/stellar-sdk';
import { useAppSelector, useAppDispatch } from '../../services/redux/hook';
import { useDelegateSigningMutation } from "../../services/api/ebioro/ebioro";
import * as Sentry from "@sentry/react";
import AppHeader from "../../components/AppHeader/AppHeader";
import PaymentProcessing from "../payment-processing/PaymentProcessing";

interface Props extends PropsWithChildren {
    fAsset: Asset;
    tAsset: Asset;
    fAssetAmount: number;
    tAssetAmount: number;
    path: any;
}


const TradeConfirmationPage: React.FC<Props> = ({ fAsset, fAssetAmount, tAsset, tAssetAmount, path }) => {

    const nav = document.querySelector("ion-nav");
    const modal = useRef<HTMLIonModalElement>(null);
    const page = useRef(null);
    const [presentingElement, setPresentingElement] = useState<HTMLElement | null>(null);
    const [disabled, setDisabled] = useState(false);

    const { t, i18n } = useTranslation();
    const walletInfo = useAppSelector((state) => state.walletInfo);

    const [isSpinner, setIsSpinner] = useState(false);
    const [present] = useIonAlert();
    const dispatch = useAppDispatch();
    const [delegateSigning] = useDelegateSigningMutation();
    const [fromAsset, setFromAsset] = useState<any>();
    const [fromAssetAmount, setFromAssetAmount] = useState(0);
    const [toAsset, setToAsset] = useState<any>();
    const [toAssetAmount, setToAssetAmount] = useState(0);
    const [trading_path, setTradingPath] = useState<[]>([]);

    const presentErrorAlert = (error: any) => {

        present({
            header: 'Error',
            message: `${error}`,
            buttons: [
                {
                    text: 'Close',
                    role: 'cancel',
                    handler: () => {
                        setIsSpinner(false)
                    }
                }
            ],

        })

    }


    const trade = async () => {

        if (fromAsset && toAsset) {

            setIsSpinner(true);
            setDisabled(true);

            let stellarTxId: string;

            try {

                if (walletInfo) {

                    if (toAsset.code != 'XLM') {

                        const [trustError, isAssetTrusted] = await stellarApi.checkAssetTrust(walletInfo?.account_key as string, toAsset.identifier.split(':')[0], toAsset.identifier.split(':')[1]);

                        if (trustError) {
                            throw trustError
                        }

                        // asset not trusted. Creating a trust before doing the trade
                        if (!isAssetTrusted) {

                            const [error, stellarTx] = await createTrustAndTradeTx(fromAsset, fromAssetAmount.toString(), toAsset, walletInfo.account_key as string, walletInfo.device_private_key as string, trading_path);

                            if (error) {
                                throw error
                            }

                            if (stellarTx) {

                                const uri = createSep7URI(stellarTx);

                                if (!uri) {
                                    setIsSpinner(false);
                                    setDisabled(false);
                                    presentErrorAlert('Not valid uri creation');
                                    return

                                }

                                let params: DelegateSigningParams = {
                                    uri: uri,
                                    account: walletInfo.account_key as string,
                                    asset_code: fromAsset.display_code,
                                    asset_issuer: fromAsset.identifier.split(':')[1]
                                }

                                const result = await delegateSigning(params).unwrap();

                                if ('error' in result) {
                                    throw result.error
                                }

                                stellarTxId = result.stellar_transaction_id as string;

                            }

                        }

                        else {

                            const [error, stellarTx] = await createTradeTx(fromAsset, fromAssetAmount.toString(), toAsset, walletInfo.account_key as string, walletInfo.device_private_key as string, trading_path);

                            if (error) {

                                throw error
                            }

                            if (stellarTx) {

                                const uri = createSep7URI(stellarTx);

                                if (!uri) {
                                    setIsSpinner(false);
                                    setDisabled(false);
                                    presentErrorAlert('Not valid uri creation');
                                    return
                                }

                                let params: DelegateSigningParams = {
                                    uri: uri,
                                    account: walletInfo.account_key as string,
                                    asset_code: fromAsset.display_code,
                                    asset_issuer: fromAsset.identifier.split(':')[1]
                                }

                                const result = await delegateSigning(params).unwrap();

                                if ('error' in result) {
                                    throw result.error
                                }

                                stellarTxId = result.stellar_transaction_id as string;
                            }

                        }
                    }

                    else {

                        const [error, stellarTx] = await createTradeTx(fromAsset, fromAssetAmount.toString(), toAsset, walletInfo.account_key as string, walletInfo.device_private_key as string, trading_path);

                        if (error) {
                            throw error
                        }

                        if (stellarTx) {

                            const uri = createSep7URI(stellarTx);

                            if (!uri) {
                                setIsSpinner(false);
                                setDisabled(false);
                                presentErrorAlert('Not valid uri creation');
                                return

                            }

                            let params: DelegateSigningParams = {
                                uri: uri,
                                account: walletInfo.account_key as string,
                                asset_code: fromAsset.display_code,
                                asset_issuer: fromAsset.identifier.split(':')[1]
                            }

                            const result = await delegateSigning(params).unwrap();

                            if ('error' in result) {
                                throw result.error

                            }

                            stellarTxId = result.stellar_transaction_id as string;
                        }

                    }


                    setIsSpinner(false);
                    nav?.push(() => <PaymentProcessing transactionId={stellarTxId} messageText={t('trade.confirmation.PROCESSING-MESSAGE') as string} successMessage={t('trade.confirmation.SUCCESS-MESSAGE') as string} />)


                }

            } catch (error: any) {

                Sentry.captureException(error);
                setIsSpinner(false);
                setDisabled(false);

                if (error.message === 'submission_error') {
                    presentErrorAlert(t('transaction.submission.ERROR'))

                }

                else {

                    presentErrorAlert(t('application.uncaught.ERROR'));
                }

                return

            }

        }

    }


    const createSep7URI = (stellarTx: stellarSdk.Transaction<stellarSdk.Memo<stellarSdk.MemoType>, stellarSdk.Operation[]>) => {

        if (walletInfo) {

            return sep7.createSep7URI(stellarTx, walletInfo.account_key as string);

        }

        return null

    }

    useEffect(() => {

        const init = async () => {

            setPresentingElement(page.current);
            setFromAsset(fAsset);
            setFromAssetAmount(fAssetAmount)
            setToAsset(tAsset);
            setToAssetAmount(tAssetAmount);
            setTradingPath(path)

        }

        init()
    }, [])

    return (

        <IonPage>
            <IonHeader className="ion-no-border">

                <IonToolbar style={{ marginTop: "0%" }}>
                    <IonNavLink routerDirection={'back'}>
                        <IonButtons slot='start'>
                            <IonButton color={'primary'}>
                                <IonIcon slot='start' icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>
                    </IonNavLink>
                    <IonTitle>{t('trade.confirmation.HEADER')} </IonTitle>

                </IonToolbar>

            </IonHeader>

            {/* <IonHeader className="ion-no-border">
                <IonToolbar >
                    <IonButtons slot="start" onClick={() => { nav?.pop() }}>
                        <IonButton >
                            <IonIcon slot="icon-only" icon={arrowBackOutline} color="danger" />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
                <IonToolbar style={{ marginTop: "0%" }}>
                <IonItem lines="none">
                    <IonText slot="start">
                        <h3>
                            {t('trade.confirmation.HEADER')}
                        </h3>
                    </IonText>
                </IonItem>
                </IonToolbar>

            </IonHeader> */}

            {

                (fromAsset && toAsset) ?

                    <IonContent>

                        <IonCard style={{marginTop:'10%'}}>
                            <IonCardContent>
                                <IonList className="bg-transparent">
                                    <IonItem lines='none'>
                                        <IonAvatar slot='start'>
                                            <img src={fromAsset.icon} />
                                        </IonAvatar>
                                        <IonLabel>
                                            <h3>{t('trade.confirmation.SEND-LABEL')}</h3>
                                            <h2>{fromAsset.display_code}</h2>
                                        </IonLabel>
                                        <IonLabel className='tradeConfirmationListAmount' >

                                            <NumberFormat value={fromAssetAmount} displayType={'text'} thousandSeparator={true} suffix={" " + fromAsset.code} decimalSeparator="." decimalScale={fromAsset.decimal_places} fixedDecimalScale={true} />

                                        </IonLabel>
                                    </IonItem>

                                    <div className='ion-text-center' >
                                        <IonIcon icon={chevronDownOutline} size='large' color='primary' />
                                    </div>

                                    <IonItem lines='none'>
                                        <IonAvatar slot='start'>
                                            <img src={toAsset.icon} />
                                        </IonAvatar>
                                        <IonLabel>
                                            <h3>{t('trade.confirmation.RECEIVE-LABEL')}</h3>
                                            <h2>{toAsset.display_code}</h2>
                                        </IonLabel>
                                        <IonLabel className='tradeConfirmationListAmount'>

                                            <NumberFormat value={toAssetAmount} displayType={'text'} thousandSeparator={true} suffix={" " + toAsset.code} decimalSeparator="." decimalScale={toAsset.decimal_places} fixedDecimalScale={true} />

                                        </IonLabel>

                                    </IonItem>
                                </IonList>
                            </IonCardContent>
                        </IonCard>

                        <IonCard className="receiveStellarPayGrid">

                            <IonCardContent>
                                <IonGrid>
                                    <IonRow>
                                        <IonCol>
                                            <IonItem lines="none">
                                                <IonIcon slot="start" icon={alertCircleOutline} color="warning"></IonIcon>
                                                <IonLabel className='ion-text-justify' >  {t('trade.confirmation.INFO')}</IonLabel>
                                            </IonItem>
                                        </IonCol>
                                    </IonRow>
                                </IonGrid>
                            </IonCardContent>
                        </IonCard>

                    </IonContent>

                    :

                    <></>
            }


            <div id='pageFooter'>
                <IonToolbar>
                    <IonButton
                        color={'primary'}
                        type="submit"
                        onClick={() => { trade() }}
                        disabled={disabled}

                        expand="block"
                        id="pageFooterButton">
                        {isSpinner ? (<IonSpinner className="BUTTON-SPINNER"></IonSpinner>)
                            : t("trade.BTN")
                        }

                    </IonButton>
                </IonToolbar>
            </div>
        </IonPage>

    )
}

export default TradeConfirmationPage